import type { ProteinCategories } from '../interfaces/myaccount';

export const PageTitles = {
  login: {
    pageTitle: 'Log into Your Account',
    headingText: 'Log In',
    descriptionText: '',
  },
  reactivation: {
    pageTitle: 'Reactivate your Chef Ami account',
    headingText: 'Claim Your $50 Reactivation Discount',
    descriptionText: 'Simply log in below and click "Yes"',
  },
  forgotPassword: 'Forgot Password - Chef Ami',
  changePassword: 'Change Password - Chef Ami',
  deliverySchedule: 'Manage Deliveries - Chef Ami',
  referral: 'Referral - Chef Ami',
  freeBoxes: 'My freebox - Chef Ami',
  myAccount: 'My Account - Chef Ami',
  myOrders: 'My orders - Chef Ami',
  orderDetails: 'Order details - Chef Ami',
  membershipSettings: 'Subscription Settings - Chef Ami',
  cancelSubscription: 'Cancel Subscription - Chef Ami',
  wallets: 'Wallets - Chef Ami',
  thankYou: 'Thank You - Chef Ami',
  donations: 'Donations - Chef Ami',
  myRatings: 'My Ratings - Chef Ami',
  updateDeliveryFrequency: 'Update Delivery Frequency - Chef Ami',
  noPageFound: 'Error - Chef Ami',
} as const;

/**
 * New account page image paths.
 * These are served from the `public/img/` folder (available at `/img/` at runtime).
 */
export const ImagePaths = {
  LOGIN_BACKGROUND: '/img/login-background.jpg',
  MEGA_PHONE_ICON: '/img/megaphone-icon.svg',
  EMAIL_ICON: '/img/email-icon.svg',
  DONATION_BANNER: '/img/donation-banner.jpg',
  DONATION_GIFT: '/img/donation-gift.png',
  DONATION_BOX: '/img/box_01.svg',
  HEADER_DONATION_BOX: '/img/box_02.svg',
  DONATION_BOX_HEART: '/img/box-heart.svg',
  CHEFAMI_LOGO: '/img/chefami-logo.png',
  GIFTCARD_ICON: '/img/giftcard-icon.svg',
  CELEBRATION_ICON: '/img/celebration.png',
  CALENDAR_ICON: '/img/calendar.png',
  LOCK_ICON: '/img/lock.png',
  SKIPPED_DELIVERY_ICON: '/img/skipped-delivery.png',
  FAILED_PAYMENT_ICON: '/img/failed-payment.png',
  CHECKED_TICK_ICON: '/img/checked-tick.svg',
  CHECKED_TICK_LIME_ICON: '/img/checked-tick-lime.svg',
  PLUS_ICON: '/img/plus-circle-icon.svg',
  MINUS_ICON: '/img/minus-circle-icon.svg',
  ARROW_UP_ICON: '/img/up-arrow-icon.svg',
  RIGHT_ARROW_ICON: '/img/right-arrow.svg',
  SELECT_ICON: '/img/select-circle.svg',
  SELECTED_ICON: '/img/selected-circle.svg',
  BEEF_ICON: '/img/RED_MEAT.svg',
  BEEF_COLOR_ICON: '/img/RED_MEAT_COLOR.svg',
  PORK_ICON: '/img/PORK.svg',
  PORK_COLOR_ICON: '/img/PORK_COLOR.svg',
  WHITE_MEAT_ICON: '/img/WHITE_MEAT.svg',
  WHITE_MEAT_COLOR_ICON: '/img/WHITE_MEAT_COLOR.svg',
  POULTRY_ICON: '/img/POULTRY.svg',
  POULTRY_COLOR_ICON: '/img/POULTRY_COLOR.svg',
  SHELLFISH_ICON: '/img/SHELLFISH.svg',
  SHELLFISH_COLOR_ICON: '/img/SHELLFISH_COLOR.svg',
  FISH_ICON: '/img/FISH.svg',
  FISH_COLOR_ICON: '/img/FISH_COLOR.svg',
  SALMON_ICON: '/img/SALMON.svg',
  SALMON_COLOR_ICON: '/img/SALMON_COLOR.svg',
  VEGETARIAN_ICON: '/img/VEGETARIAN.svg',
  VEGETARIAN_COLOR_ICON: '/img/VEGETARIAN_COLOR.svg',
} as const;

export const PROTEIN_CATEGORIES: ProteinCategories[] = [
  {
    id: 0,
    name: 'Beef',
    description: '',
    slug: 'beef',
    normal_image: ImagePaths.BEEF_ICON,
    active_image: ImagePaths.BEEF_COLOR_ICON,
  },
  {
    id: 0,
    name: 'Pork',
    description: '',
    slug: 'pork',
    normal_image: ImagePaths.PORK_ICON,
    active_image: ImagePaths.PORK_COLOR_ICON,
  },
  {
    id: 0,
    name: 'Poultry (white meat)',
    description: 'white meat',
    slug: 'poultry_white',
    normal_image: ImagePaths.WHITE_MEAT_ICON,
    active_image: ImagePaths.WHITE_MEAT_COLOR_ICON,
  },
  {
    id: 0,
    name: 'Poultry (dark meat)',
    description: 'dark meat',
    slug: 'poultry_dark',
    normal_image: ImagePaths.POULTRY_ICON,
    active_image: ImagePaths.POULTRY_COLOR_ICON,
  },
  {
    id: 0,
    name: 'Shellfish',
    description: '',
    slug: 'shellfish',
    normal_image: ImagePaths.SHELLFISH_ICON,
    active_image: ImagePaths.SHELLFISH_COLOR_ICON,
  },
  {
    id: 0,
    name: 'Fish',
    description: '',
    slug: 'fish',
    normal_image: ImagePaths.FISH_ICON,
    active_image: ImagePaths.FISH_COLOR_ICON,
  },
  {
    id: 0,
    name: 'Salmon',
    description: '',
    slug: 'salmon',
    normal_image: ImagePaths.SALMON_ICON,
    active_image: ImagePaths.SALMON_COLOR_ICON,
  },
  {
    id: 0,
    name: 'Vegetarian',
    description: '',
    slug: 'vegetarian',
    normal_image: ImagePaths.VEGETARIAN_ICON,
    active_image: ImagePaths.VEGETARIAN_COLOR_ICON,
  },
];

export const SubscriptionStatus = {
  PAUSED: 0,
  ACTIVE: 1,
  CANCELLED: 2,
  PERMANENT_PAUSED: 3,
} as const;

export const OrderTopText = {
  STATUS: {
    0: 'Payment failed, click',
    1: 'Your order is in production, no further changes can be made',
    2: 'Your order has been delivered',
    3: 'Your order has been cancelled',
    4: 'Your order is out for delivery!',
    5: 'Your order is in production, no further changes can be made',
  } as Readonly<Record<number, string>>,
  HOLIDAY: 'We are on holiday!',
  PAYMENT_PROCESSING:
    'Our system is busy processing orders, please check back later',
  SKIPPED: 'Skipped',
  SCHEDULED: 'Scheduled',
  CAN_MAKE_CHANGES: (date: string | null) =>
    `You can make changes to this order until midnight ${date}`,
  SKIPPED_AFTER_CUTOFF: (date: string | null) =>
    `You have skipped your ${date} delivery`,
} as const;
